/**
 * Single source of truth for all wallet analytics event names.
 *
 * Every wallet event tracked in the application must be listed here.
 * The coverage test in `wallet-analytics-events.spec.ts` verifies that
 * each event name appears in at least one `.track()` call in the codebase.
 *
 * Should maintain parity to
 * https://www.notion.so/republic/Global-Wallet-Analytics-0ff86567bd75809584c8f1c0eb904963
 */

export const WALLET_EVENTS = {
  BACKUP: {
    QR_VIEWED: 'wallet_creation_backup_qr_viewed',
    QR_SCANNED: 'wallet_creation_backup_qr_scanned',
    NO_SECOND_DEVICE_SELECTED:
      'wallet_creation_backup_no_second_device_selected',
    MODAL_OPENED: 'wallet_creation_backup_modal_opened',
    MODAL_CONTINUE_SELECTED: 'wallet_creation_backup_modal_continue_selected',
    MODAL_TRY_ANOTHER_DEVICE: 'wallet_creation_backup_modal_try_another_device',
    PASSKEY_PAGE_OPENED: 'wallet_creation_backup_passkey_page_opened',
    PASSKEY_INITIATED: 'wallet_creation_backup_passkey_initiated',
    WALLET_STORED_LOCALLY: 'wallet_creation_backup_wallet_stored_locally',
    COMPLETE: 'wallet_creation_backup_complete',
  },
  CREATION: {
    ENTERED: 'wallet_creation_wallet_creation_entered',
    MFA_STARTED: 'wallet_creation_mfa_started',
    MFA_SUCCEEDED: 'wallet_creation_mfa_succeeded',
    MFA_FAILED: 'wallet_creation_mfa_failed',
    NEW_WALLET_SELECTED: 'wallet_creation_new_wallet_selected',
    ENCRYPTION_CREATED: 'wallet_creation_encryption_created',
    SEED_PHRASE_VIEWED: 'wallet_creation_seed_phrase_viewed',
    SEED_PHRASE_DOWNLOADED: 'wallet_creation_seed_phrase_downloaded',
    SEED_PHRASE_COPIED: 'wallet_creation_seed_phrase_copied',
    SEED_PHRASE_CONFIRMATION: 'wallet_creation_seed_phrase_confirmation',
    LEGAL_DOC_CHECKED: 'wallet_creation_legal_doc_checked',
    SETUP_FINISHED: 'wallet_creation_setup_finished',
    OPEN_WALLET_CLICKED: 'wallet_creation_open_wallet_clicked',
    STATE: 'wallet_creation_state',
    PASSKEY_NOT_SUPPORTED: 'wallet_creation_passkey_not_supported',
  },
  CREATION_IMPORT: {
    SELECTED: 'wallet_creation_import_selected',
    MFA_STARTED: 'wallet_creation_import_mfa_started',
    MFA_SUCCEEDED: 'wallet_creation_import_mfa_succeeded',
    MFA_FAILED: 'wallet_creation_import_mfa_failed',
    SEED_PHRASE_CONFIRMATION: 'wallet_creation_import_seed_phrase_confirmation',
    ENCRYPTION_CREATED: 'wallet_creation_import_encryption_created',
    STATE: 'wallet_creation_import_state',
  },
  CUSTOM_TOKEN: {
    DRAWER_OPENED: 'wallet_custom_token_drawer_opened',
    TOKEN_ADDED: 'wallet_custom_token_added',
  },
  FEATURES: {
    ADD_TO_WALLET_CLICKED: 'wallet_add_to_wallet_clicked',
    ASSET_VISIBILITY_TOGGLED: 'wallet_asset_visibility_toggled',
    KEYS_NOT_DETECTED: 'wallet_keys_not_detected',
    CUSTOM_TOKEN_DRAWER_OPENED: 'wallet_custom_token_drawer_opened',
  },
  ONRAMP: {
    BUY_CLICKED: 'wallet_onramp_buy_clicked',
    DETAILS_ENTERED: 'wallet_onramp_details_entered',
    PROVIDER_CHOSEN: 'wallet_onramp_provider_chosen',
    ERROR_STATE: 'wallet_onramp_error_state',
  },
  RECOVERY: {
    SELECTED: 'wallet_recover_selected',
    SEED_PHRASE_CONFIRMATION: 'wallet_recover_seed_phrase_confirmation',
    ENCRYPTION_CREATED: 'wallet_recover_encryption_created',
    STATE: 'wallet_recover_state',
  },
  SWAP: {
    DRAWER_OPENED: 'wallet_swap_drawer_opened',
    DRAWER_CLOSED: 'wallet_swap_drawer_closed',
    QUOTE_FAILED: 'wallet_swap_quote_failed',
    QUOTE_RECEIVED: 'wallet_swap_quote_received',
    QUOTE_REQUESTED: 'wallet_swap_quote_requested',
    REVIEW_OPENED: 'wallet_swap_review_opened',
    SUBMISSION_FAILED: 'wallet_swap_submission_failed',
    SUBMISSION_STARTED: 'wallet_swap_submission_started',
    SUBMISSION_SUCCEEDED: 'wallet_swap_submission_succeeded',
    DESTINATION_ASSET_ADDED: 'wallet_swap_destination_asset_added',
  },
  TRANSACTIONS: {
    INITIATED: 'wallet_transaction_initiated',
    SIGN_REQUESTED: 'wallet_transaction_sign_requested',
    SIGN_FAILED: 'wallet_transaction_sign_failed',
    SIGNED: 'wallet_transaction_signed',
    COMPLETED: 'wallet_transaction_completed',
  },
} as const;

/** Flat array of every wallet event name — used by the analytics coverage test. */
export const ALL_WALLET_EVENT_NAMES = flattenEventNames(WALLET_EVENTS);

type NestedRecord = { readonly [key: string]: string | NestedRecord };

function flattenEventNames(obj: NestedRecord): string[] {
  const result: string[] = [];

  for (const value of Object.values(obj)) {
    if (typeof value === 'string') {
      result.push(value);
    } else {
      result.push(...flattenEventNames(value));
    }
  }

  return result;
}
